"use client";

import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import type React from "react";
import { useState, useEffect } from "react";
import axios from "axios";

// Material UI
import {
  Box,
  Typography,
  Button,
  Dialog,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Fab,
  Stack,
  Badge,
  Pagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  InputAdornment,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import InboxIcon from "@mui/icons-material/Inbox";
import OutboxIcon from "@mui/icons-material/Outbox";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import { baseUrl, config, requestID } from "../../common/utils";

// Styled components for better visual hierarchy
const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  "& .MuiTab-root": {
    minHeight: 64,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
  },
}));

const MessageCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: "all 0.2s ease-in-out",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: theme.spacing(1.5),
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  "&.unread": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
  },
}));

const ComposeDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    minHeight: "500px",
  },
}));

const FloatingCompose = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}));

const InAppMesssging: React.FC = () => {
  // Messages
  const [inbox, setInbox] = useState<any[]>([]);
  const [sent, setSent] = useState<any[]>([]);
  const [filteredInbox, setFilteredInbox] = useState<any[]>([]);
  const [filteredSent, setFilteredSent] = useState<any[]>([]);

  // UI states
  const [tab, setTab] = useState(1); // 0 = Compose, 1 = Inbox, 2 = Sent
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false); // for send button

  // Compose form states
  const [to, setTo] = useState<any>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [unread, setUnRead] = useState("");

  // Pagination states
  const [inboxPage, setInboxPage] = useState(1);
  const [sentPage, setSentPage] = useState(1);
  const itemsPerPage = 10;

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filter, setFilter] = useState("all"); // all, unread, read

  // Message menu states
  const [messageMenuAnchor, setMessageMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    // Filter messages based on search query and filter type
    if (tab === 1) {
      let filtered = inbox;

      // Apply search filter
      if (searchQuery) {
        filtered = filtered.filter(
          (msg) =>
            msg.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            msg.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            msg.firstName?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Apply status filter
      if (filter === "unread") {
        filtered = filtered.filter((msg) => msg.isRead === "0");
      } else if (filter === "read") {
        filtered = filtered.filter((msg) => msg.isRead === "1");
      }

      setFilteredInbox(filtered);
      setInboxPage(1); // Reset to first page when filters change
    } else if (tab === 2) {
      let filtered = sent;

      // Apply search filter
      if (searchQuery) {
        filtered = filtered.filter(
          (msg) =>
            msg.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            msg.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            msg.firstName?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setFilteredSent(filtered);
      setSentPage(1); // Reset to first page when filters change
    }
  }, [searchQuery, filter, inbox, sent, tab]);

  async function loadAllData() {
    try {
      setPageLoading(true);
      await Promise.all([
        getAllUsers(),
        getAllInbox(),
        getAllMessage(),
        getNumberOfUnread(),
        markRead(),
      ]);
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setPageLoading(false);
    }
  }

  // Get new messages
  async function getNumberOfUnread() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Appmessages/getMessageNewInbox",
        formData,
        config
      );
      setUnRead(response.data.data);
      console.log("unread", response);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }

  // Mark read
  async function markRead() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Appmessages/markRead",
        formData,
        config
      );
      console.log("read", response);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }

  // Load users (you can later make this endpoint support search & pagination)
  async function getAllUsers(query = "") {
    try {
      setSearching(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      if (query) formData.append("search", query); // backend should filter

      const response = await axios.post(
        baseUrl() + "businesss/getAllStaffs2",
        formData,
        config
      );
      setUsers(response.data.data || []);
    } catch (error) {
      console.error("Failed to load users:", error);
    } finally {
      setSearching(false);
    }
  }

  // Send message
  async function handleSend() {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("receiverID", to?.user_id || "");
      formData.append("title", subject);
      formData.append("message", body);

      const response = await axios.post(
        baseUrl() + "Appmessages/sendmessage",
        formData,
        config
      );
      console.log("Message sent:", response.data);

      // Refresh inbox/sent
      getAllInbox();
      getAllMessage();
      getNumberOfUnread();

      // Reset form & close popup
      setTo(null);
      setSubject("");
      setBody("");
      setTab(1); // Switch to inbox tab
    } catch (error) {
      console.error("Send failed:", error);
    } finally {
      setLoading(false);
    }
  }

  // Load inbox
  async function getAllInbox() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Appmessages/getMessagebyInbox",
        formData,
        config
      );
      setInbox(response.data.data || []);
      setFilteredInbox(response.data.data || []);
      console.log("inbox", response || []);
    } catch (error) {
      console.error("Inbox fetch failed:", error);
    }
  }

  // Load sent
  async function getAllMessage() {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Appmessages/getMessagebySent",
        formData,
        config
      );
      setSent(response.data.data || []);
      setFilteredSent(response.data.data || []);
    } catch (error) {
      console.error("Sent fetch failed:", error);
    }
  }

  // Handle message menu open
  const handleMessageMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    message: any
  ) => {
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  // Handle message menu close
  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessage(null);
  };

  // Handle message actions
  const handleMessageAction = (action: string) => {
    console.log(`Performing ${action} on message:`, selectedMessage);
    // Implement message actions here (delete, mark as unread, archive, etc.)
    handleMessageMenuClose();
  };

  // Calculate paginated messages
  const getPaginatedMessages = () => {
    if (tab === 1) {
      const startIndex = (inboxPage - 1) * itemsPerPage;
      return filteredInbox.slice(startIndex, startIndex + itemsPerPage);
    } else if (tab === 2) {
      const startIndex = (sentPage - 1) * itemsPerPage;
      return filteredSent.slice(startIndex, startIndex + itemsPerPage);
    }
    return [];
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  return (
    <IonPage className="pushDown">
      {/* Ionic Header */}
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Mailbox</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          maxWidth="900px"
          mx="auto"
          height="100vh"
          display="flex"
          flexDirection="column"
          sx={{ backgroundColor: "background.default", position: "relative" }}
        >
          {pageLoading && (
            <LoadingOverlay>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading your messages...
              </Typography>
            </LoadingOverlay>
          )}

          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              p: 3,
              borderRadius: "0 0 24px 24px",
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h4" fontWeight="700" gutterBottom>
                  Mailbox
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Stay connected with your team
                </Typography>
              </Box>
              {Number(unread) > 0 && (
                <Badge badgeContent={unread} color="error">
                  <EmailIcon sx={{ fontSize: 40 }} />
                </Badge>
              )}
            </Stack>
          </Box>

          <StyledTabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            centered
            sx={{ mx: 2, borderRadius: 2, mb: 2 }}
          >
            <Tab icon={<EditIcon />} label="Compose" iconPosition="start" />
            <Tab
              icon={<InboxIcon />}
              label={`Inbox (${inbox.length})`}
              iconPosition="start"
            />
            <Tab
              icon={<OutboxIcon />}
              label={`Sent (${sent.length})`}
              iconPosition="start"
            />
          </StyledTabs>

          {/* Search and Filter Bar */}
          {(tab === 1 || tab === 2) && (
            <Paper elevation={0} sx={{ p: 2, mx: 2, mb: 2, borderRadius: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search messages..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton onClick={() => loadAllData()}>
                  <RefreshIcon />
                </IconButton>
                {tab === 1 && (
                  <>
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                      <FilterListIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                    >
                      <MenuItem
                        selected={filter === "all"}
                        onClick={() => {
                          setFilter("all");
                          setAnchorEl(null);
                        }}
                      >
                        All Messages
                      </MenuItem>
                      <MenuItem
                        selected={filter === "unread"}
                        onClick={() => {
                          setFilter("unread");
                          setAnchorEl(null);
                        }}
                      >
                        Unread Only
                      </MenuItem>
                      <MenuItem
                        selected={filter === "read"}
                        onClick={() => {
                          setFilter("read");
                          setAnchorEl(null);
                        }}
                      >
                        Read Only
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            </Paper>
          )}

          {tab === 0 && (
            <Box p={2} flex={1} overflow="auto">
              <Card
                elevation={0}
                sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="600" gutterBottom>
                    Compose Message
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={3}>
                    <Autocomplete
                      options={users}
                      getOptionLabel={(option: any) =>
                        `${option.firstName} (${
                          option.email || option.user_id
                        })`
                      }
                      loading={searching}
                      value={to}
                      onChange={(_, value) => setTo(value)}
                      onInputChange={(_, value) => {
                        if (value.length > 1) getAllUsers(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To"
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {searching ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Avatar
                            sx={{
                              mr: 2,
                              bgcolor: "primary.main",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {option.firstName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.email || option.user_id}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />

                    <TextField
                      fullWidth
                      label="Subject"
                      variant="outlined"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />

                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      label="Message"
                      variant="outlined"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Type your message here..."
                    />

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => {
                          setTo(null);
                          setSubject("");
                          setBody("");
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={
                          loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SendIcon />
                          )
                        }
                        onClick={handleSend}
                        disabled={loading || !to || !subject || !body}
                        sx={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                          },
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}

          {(tab === 1 || tab === 2) && (
            <Box p={2} flex={1} overflow="auto">
              {getPaginatedMessages().length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="300px"
                  sx={{ opacity: 0.6 }}
                >
                  {tab === 1 ? (
                    <InboxIcon sx={{ fontSize: 64, mb: 2 }} />
                  ) : (
                    <OutboxIcon sx={{ fontSize: 64, mb: 2 }} />
                  )}
                  <Typography variant="h6">
                    {searchQuery || filter !== "all"
                      ? "No messages match your search"
                      : `No ${tab === 1 ? "inbox" : "sent"} messages`}
                  </Typography>
                  <Typography variant="body2">
                    {searchQuery || filter !== "all"
                      ? "Try adjusting your search or filters"
                      : tab === 1
                      ? "Your inbox is empty"
                      : "You haven't sent any messages yet"}
                  </Typography>
                </Box>
              ) : (
                <>
                  {getPaginatedMessages().map((msg, i) => (
                    <MessageCard
                      key={i}
                      elevation={0}
                      className={msg.isRead === "0" ? "unread" : ""}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Avatar
                            sx={{
                              bgcolor:
                                tab === 1 ? "primary.main" : "secondary.main",
                              width: 40,
                              height: 40,
                            }}
                          >
                            {tab === 1 ? <PersonIcon /> : <SendIcon />}
                          </Avatar>
                          <Box flex={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={1}
                            >
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="600"
                                >
                                  {tab === 1
                                    ? msg.firstName
                                    : `To: ${msg.firstName}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {formatDate(msg.created_at)}
                                </Typography>
                              </Box>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                {msg.isRead === "0" && tab === 1 && (
                                  <Chip
                                    label="New"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMessageMenuOpen(e, msg)}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Stack>
                            </Stack>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ color: "text.primary", fontSize: "1.1rem" }}
                            >
                              {msg.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {msg.message}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </MessageCard>
                  ))}

                  {/* Pagination */}
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={Math.ceil(
                        (tab === 1
                          ? filteredInbox.length
                          : filteredSent.length) / itemsPerPage
                      )}
                      page={tab === 1 ? inboxPage : sentPage}
                      onChange={(_, value) =>
                        tab === 1 ? setInboxPage(value) : setSentPage(value)
                      }
                      color="primary"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Message Action Menu */}
          <Menu
            anchorEl={messageMenuAnchor}
            open={Boolean(messageMenuAnchor)}
            onClose={handleMessageMenuClose}
          >
            <MenuItem onClick={() => handleMessageAction("delete")}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
            {tab === 1 && selectedMessage?.isRead === "1" && (
              <MenuItem onClick={() => handleMessageAction("markUnread")}>
                <ListItemIcon>
                  <MarkAsUnreadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mark as unread</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => handleMessageAction("archive")}>
              <ListItemIcon>
                <ArchiveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Archive</ListItemText>
            </MenuItem>
          </Menu>

          <FloatingCompose
            color="primary"
            onClick={() => setTab(0)}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
              },
            }}
          >
            <AddIcon />
          </FloatingCompose>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default InAppMesssging;
